import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Typography, Button, Row, Col, Divider, Dropdown, Menu } from "antd";
import { DocumentFileRow, journalsQueries } from "@/entities/journal";
import { $journalApi } from "@/entities/journal/fetch/fetch-client";
import { ProjectParticipantInfo } from "@/entities/project";
import { useValidateTitle } from "@/features/journal-title-validation";
import { RepresentativeModalOrgs } from "@/features/representation-orgs-modal";
import { BasketIcon, MoreOneIcon } from "@/shared/assets/svg/icons";
import { Can } from "@/shared/lib/casl";
import { useCoordinator, useDisclosure } from "@/shared/lib/hooks";
import { queryClient } from "@/shared/lib/react-query";
import { fallbackString, formatAdministrativeDocument } from "@/shared/utils";
import type { components } from "@entities/journal";
import { ParticipantsInfiniteScroll } from "@features/participants-infinite-scroll";
import { useGetRepresentative } from "../../api";
import {
  useCreateParticipant,
  useDeleteParticipant,
  useTitleAmendmentMode,
} from "../../model";
import { CollapsibleSection } from "../collapsible-section";
import { createValidationErrorsGetter } from "../journals-title/model/get-validation-errors-for-section";
import { RepresentativesSignatureWrapper } from "../representatives-signature-wrapper";

export const OtherDevelopersTableNew = () => {
  const { titleId } = useCoordinator();
  const { projectId = "" } = useCoordinator();

  const { errorList } = useValidateTitle();

  const [selectedOrganization, setSelectedOrganization] = useState<{
    otherDeveloperListId?: string;
    projectParticipantId?: string;
    company?: components["schemas"]["Company"];
    representativeListId?: string;
    representatives?: components["schemas"]["OtherDeveloperRepresentative"][];
  } | null>(null);

  const { representative: otherDevelopers, isFetching } =
    useGetRepresentative("otherDevelopers");

  const validationErrors = useMemo(() => {
    if (!otherDevelopers || otherDevelopers.length === 0) {
      return [];
    }
    return errorList.filter((error) => [21, 32].includes(error.id));
  }, [errorList, otherDevelopers]);

  const getValidationErrorsForSection = useMemo(
    () => createValidationErrorsGetter(errorList),
    [errorList],
  );

  const { data } = useQuery(journalsQueries.getTitleJournal({ titleId }));
  const titleStatus =
    data?.mainJournal?.titleJournal?.status === "NEW" &&
    // @ts-expect-error versionNumber отсутствует в openapi типе TitleJournalViewDto
    !data?.mainJournal?.titleJournal?.versionNumber;
  const { isNewAmendment } = useTitleAmendmentMode();
  const canAddData = titleStatus || isNewAmendment;

  const { mutate: removeParticipant, isPending } = $journalApi.useMutation(
    "delete",
    "/api/v1/journals/title/{titleId}/representatives",
    {
      body: (data: any) => data,
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: journalsQueries.getTitleJournal({ titleId }).queryKey,
        });
      },
    },
  );

  const { isOpen, onOpen, onClose } = useDisclosure();

  const {
    mutate: createParticipant,
    isPending: isLoadingCreate,
    isSuccess: isSuccessCreate,
  } = useCreateParticipant({
    titleId,
  });
  const [key, setKey] = useState(0);

  const { mutate: deleteParticipant, isPending: isLoadingDelete } =
    useDeleteParticipant({ titleId });

  const onPartySelect = (participant: ProjectParticipantInfo) => {
    if (!participant) return;

    createParticipant({
      participantType: "otherDevelopers",
      companyId: [participant.company.companyId ?? ""],
      projectParticipantId: [participant.projectParticipantId ?? ""],
    });
  };

  const onDeleteOrganization = (id: string) => {
    const listId = otherDevelopers[0].otherDeveloperListId;

    const updatedParticipants =
      otherDevelopers.filter(
        (participant) => participant.projectParticipantId === id,
      ) || [];

    deleteParticipant({
      participantType: "otherDevelopers",
      projectParticipantId: updatedParticipants.map(
        (p) => p.projectParticipantId ?? "",
      ),
      listId,
    });
  };

  const handleDeleteRepresentative = (
    representativeId: string,
    participantId: string,
  ) => {
    removeParticipant({
      params: {
        path: {
          titleId,
        },
      },
      body: {
        otherDevelopers: [
          {
            projectParticipantId: participantId,
            representatives: [{ representativeId }],
          },
        ],
      },
    });
  };

  const menu = (id: string) => (
    <Menu>
      <Menu.Item
        key="delete"
        icon={<BasketIcon />}
        onClick={() => onDeleteOrganization(id)}
      >
        Удалить
      </Menu.Item>
    </Menu>
  );

  useEffect(() => {
    setKey((prev) => prev + 1);
  }, [isSuccessCreate, onClose]);

  return (
    <CollapsibleSection
      title="Другие лица, осуществляющие строительство, их уполномоченные представители"
      spinning={isFetching || isLoadingDelete || isPending}
      validationErrors={validationErrors}
      sectionId="other-developers"
      getValidationErrorsForSection={getValidationErrorsForSection}
      isEmpty={!otherDevelopers || otherDevelopers.length === 0}
    >
      <>
        {otherDevelopers?.map((developer) => (
          <div
            key={developer.projectParticipantId}
            className="journal-title-participant-section"
          >
            <div className="journal-title-participant-header">
              <Typography.Title level={5}>
                {fallbackString(developer.company?.fullName)}
              </Typography.Title>
              {developer?.projectParticipantId &&
              titleStatus &&
              !isNewAmendment ? (
                <Can I="edit" a="title_journals">
                  <Dropdown
                    overlay={menu(developer.projectParticipantId)}
                    trigger={["click"]}
                  >
                    <span className="journal-title-menu-trigger">
                      <MoreOneIcon width={24} height={24} />
                    </span>
                  </Dropdown>
                </Can>
              ) : null}
            </div>
            <Row gutter={[16, 16]} style={{ marginTop: "16px" }}>
              <Col span={8} style={{ color: "#888888" }}>
                ИНН
              </Col>
              <Col span={16}>{fallbackString(developer.company?.inn)}</Col>
              <Col span={8} style={{ color: "#888888" }}>
                ОГРН
              </Col>
              <Col span={16}>{fallbackString(developer.company?.ogrn)}</Col>
              <Col span={8} style={{ color: "#888888" }}>
                Контакты
              </Col>
              <Col span={16}>
                {fallbackString(
                  [
                    developer.company?.contacts?.phone,
                    developer.company?.contacts?.email,
                  ]
                    .filter(Boolean)
                    .join(", "),
                )}
              </Col>
              <Col span={8} style={{ color: "#888888" }}>
                Адрес
              </Col>
              <Col span={16}>
                {fallbackString(
                  developer.company?.addresses?.legal?.fullAddress,
                )}
              </Col>
            </Row>

            {canAddData ? (
              <>
                <div className="journal-title-participant-block">
                  <div>
                    <Can I="edit" a="title_journals">
                      <Button
                        type={
                          developer.representatives?.length
                            ? "default"
                            : "primary"
                        }
                        style={{
                          marginTop: "16px",
                          marginBottom: "24px",
                          // @ts-expect-error несовпадение типов требует ручной проверки openapi
                          backgroundColor:
                            developer.representatives?.length &&
                            "rgba(234, 238, 248, 1)",
                        }}
                        onClick={() => {
                          setSelectedOrganization(developer);
                          onOpen();
                        }}
                      >
                        Добавить сотрудника
                      </Button>
                    </Can>
                  </div>
                </div>
              </>
            ) : null}
            {developer.representatives?.length
              ? developer.representatives.map((rep) => (
                  <RepresentativesSignatureWrapper
                    representative={rep}
                    representativeType="otherDevelopers"
                  >
                    <div
                      key={rep.representativeId}
                      className="journal-title-rep-card"
                    >
                      <div className="journal-title-rep-card__header">
                        <Typography.Title
                          level={5}
                          className="journal-title-rep-card__name"
                        >
                          {fallbackString(
                            `${rep.user?.surname || ""} ${rep.user?.firstName || ""} ${rep.user?.middleName || ""}`.trim(),
                          )}
                        </Typography.Title>
                        {titleStatus && !isNewAmendment ? (
                          <Can I="edit" a="title_journals">
                            <Dropdown
                              overlay={
                                <Menu>
                                  <Menu.Item
                                    key="delete"
                                    icon={
                                      <div>
                                        <BasketIcon />
                                      </div>
                                    }
                                    onClick={() =>
                                      handleDeleteRepresentative(
                                        rep.representativeId ?? "",
                                        developer.projectParticipantId ?? "",
                                      )
                                    }
                                  >
                                    Удалить
                                  </Menu.Item>
                                </Menu>
                              }
                              trigger={["click"]}
                            >
                              <div className="journal-title-menu-trigger">
                                <MoreOneIcon width={24} height={24} />
                              </div>
                            </Dropdown>
                          </Can>
                        ) : null}
                      </div>
                      <Row gutter={[16, 16]} style={{ marginTop: "16px" }}>
                        {/* 1. Организация */}
                        <Col span={8} style={{ color: "#888888" }}>
                          Организация
                        </Col>
                        <Col span={16}>
                          {fallbackString(
                            rep?.administrativeDocument?.organization,
                          )}
                        </Col>
                        {/* 2. Должность */}
                        <Col span={8} style={{ color: "#888888" }}>
                          Должность
                        </Col>
                        <Col span={16}>
                          {fallbackString(
                            (
                              rep as typeof rep & {
                                representativeRole?: string;
                              }
                            ).representativeRole,
                          )}
                        </Col>
                        {/* 3. Почта */}
                        <Col span={8} style={{ color: "#888888" }}>
                          Почта
                        </Col>
                        <Col span={16}>
                          {rep.user?.userEmail ? (
                            <a href={`mailto:${rep.user.userEmail}`}>
                              {rep.user.userEmail}
                            </a>
                          ) : (
                            "-"
                          )}
                        </Col>
                        {/* 4. Телефон */}
                        <Col span={8} style={{ color: "#888888" }}>
                          Телефон
                        </Col>
                        <Col span={16}>
                          {fallbackString(rep.user?.mobilePhone)}
                        </Col>
                        {/* 5. Документ, №, дата */}
                        <Col span={8} style={{ color: "#888888" }}>
                          Документ, №, дата
                        </Col>
                        <Col span={16} className="journal-title-rep-doc-cell">
                          {(() => {
                            const doc = rep?.administrativeDocument;
                            if (!doc?.number) {
                              return "-";
                            }
                            if (
                              doc.administrativeDocumentId &&
                              doc.documentUrl
                            ) {
                              return (
                                <DocumentFileRow
                                  inline
                                  variant="link"
                                  downloadIconPosition="right"
                                  fileName={
                                    formatAdministrativeDocument(doc) ||
                                    "document.pdf"
                                  }
                                  documentUrl={doc.documentUrl}
                                />
                              );
                            }
                            return fallbackString(
                              formatAdministrativeDocument(doc),
                            );
                          })()}
                        </Col>
                        {/* 6. Номер в НОПРИЗ */}
                        <Col span={8} style={{ color: "#888888" }}>
                          Номер в НОПРИЗ
                        </Col>
                        <Col span={16}>
                          {fallbackString(
                            rep.user?.specialistNumberEngineering,
                          )}
                        </Col>
                        {/* 7. Номер в НОСТРОЙ */}
                        <Col span={8} style={{ color: "#888888" }}>
                          Номер в НОСТРОЙ
                        </Col>
                        <Col span={16}>
                          {fallbackString(rep.user?.specialistNumberBuilding)}
                        </Col>
                        {/* 8. Выполняемые работы */}
                        <Col span={8} style={{ color: "#888888" }}>
                          Выполняемые работы
                        </Col>
                        <Col span={16}>{fallbackString(rep.activityName)}</Col>
                      </Row>
                    </div>
                  </RepresentativesSignatureWrapper>
                ))
              : null}
          </div>
        ))}

        {canAddData ? (
          <Can I="edit" a="title_journals">
            <Divider />
            <div className="journal-title-add-org-form">
              <Typography.Text>Организация</Typography.Text>

              <ParticipantsInfiniteScroll
                style={{
                  width: "fit-content",
                  flex: "0 0",
                }}
                projectId={projectId}
                onPartySelect={onPartySelect}
                key={key}
                isLoading={isLoadingCreate}
              />
            </div>
          </Can>
        ) : null}

        <RepresentativeModalOrgs
          organization={{
            companyId: selectedOrganization?.company?.companyId ?? "",
            projectParticipantId:
              selectedOrganization?.projectParticipantId ?? "",
            userCompaniesHistoryId:
              selectedOrganization?.company?.companyHistoryId ?? "",
            representativeType: "otherDevelopers",
            isConstructionControl: false,
          }}
          isOpen={isOpen}
          handleClose={onClose}
          isArray
          isOtherDeveloper
          key={`${key}-${new Date().getTime()}`}
          isAddFromOldService
          enableMultiAdd
        />
      </>
    </CollapsibleSection>
  );
};
